import { sInputKeyValidationHelper } from './shared.min.js';

// Name of the analytics queue on `window`. Both builds use the page's own GTM dataLayer,
// which is what this has always been. The name is configurable only as an escape hatch, for
// a future host page whose dataLayer we must not touch. See ASK-90.
const DEFAULT_DATA_LAYER_NAME = 'dataLayer';

// User-facing strings. The matching Sitecore fields (No_Answer_Found_Text,
// Timeout_Error_Text, Default_Error_Text) are declared in ask-confused-ai.cshtml but were
// never wired into this class, so these values are what actually ships today. They stay
// here as defaults, which keeps confused.com unchanged; ASK-91 supplies overrides once the
// config endpoint exists.
const DEFAULT_COPY = {
    noAnswer: "Sorry, I couldn't find an answer to that. Please try rephrasing your question.",
    timeout: 'The request timed out. Please try again.',
    error: 'Something went wrong. Please try again later.',
    choicesHeading: 'Other people asked:',
    // ASK-554. These are the accessible NAMES of the two buttons, not decoration: a screen
    // reader announces these instead of "thumbs up sign". They say what the control does
    // rather than what it looks like, so they survive the icon being replaced.
    feedbackGroupLabel: 'Was this answer helpful?',
    feedbackUpLabel: 'This answer was helpful',
    feedbackDownLabel: 'This answer was not helpful',
};

class ChatInterface {
    constructor(config = {}) {
        const container = config.container;
        this.container = container;
        this.chatMessages = container.querySelector('#ac-chatMessages');
        this.scrollContainer = container.querySelector('.mod-ask-confused-ai__content');
        this.userInput = container.querySelector('#ac-userInput');
        this.sendButton = container.querySelector('#ac-sendButton');
        this.form = container.querySelector('.mod-ask-confused-ai__form');
        this.initialQuestionButtons = container.querySelectorAll('.initial-questions__button');
        this.choiceButtons = [];
        this.apiUrl = config.apiUrl || '';
        // ASK-554: where a rating is POSTed. Supplied as its own resolved URL by the
        // host (data-feedback-url), never derived from apiUrl - that is a complete URL
        // ending in /orchestrator, so string-munging it would break silently if either
        // path changed. Empty when the host did not supply one, which the control
        // treats as "cannot submit" rather than guessing an address.
        this.feedbackUrl = config.feedbackUrl || '';
        this.apiSubKey = config.apiSubKey || '';
        this.userJwt = config.userJwt || '';
        // Only a non-blank string is usable as a window key, so anything else falls back to
        // the default rather than being stored as given. A plain || is not enough: a
        // whitespace-only name is truthy and would be kept, and window['   '] is undefined,
        // which makes pushToDataLayer drop every event in silence instead of writing to
        // dataLayer. Same rule as the copy overrides below, and it must agree with the
        // resolution in standalone.js, which needs the name before this runs so it can
        // create the queue.
        const requestedQueue = config.dataLayerName;
        this.dataLayerName =
            typeof requestedQueue === 'string' && requestedQueue.trim() !== ''
                ? requestedQueue.trim()
                : DEFAULT_DATA_LAYER_NAME;
        // Overrides are read by walking DEFAULT_COPY's keys, not the caller's. That means
        // only known keys are ever considered, so nothing arbitrary lands on this object.
        // A value must also be a string with something in it once trimmed: a CMS field
        // holding only whitespace is as useless to a customer as an empty one, and must
        // not replace a message they need to read.
        const overrides = config.copy || {};
        this.copy = { ...DEFAULT_COPY };
        Object.keys(DEFAULT_COPY).forEach(key => {
            const value = overrides[key];
            if (typeof value === 'string' && value.trim() !== '') {
                this.copy[key] = value;
            }
        });
        this.conversationId = sessionStorage.getItem('askConfusedConversationId') || null;
        // ASK-714: whether the per-response feedback control may render. Starts
        // false and only ever becomes true because the server said so on a real
        // response, so the feature is off until proven on — a gate that cannot
        // resolve must never be the reason something appears.
        this.feedbackControlEnabled = false;
        // ASK-554: the in-flight rating POST per response id, so a re-rating is sent
        // only once the previous one has settled. See submitFeedback.
        this.feedbackInFlight = new Map();
        sInputKeyValidationHelper(this.userInput, 'freeform');
        this.initEventListeners();
    }


    initEventListeners() {
        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.sendMessage();
        });

        this.initialQuestionButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const question = (button.dataset.question || button.textContent || '').trim();
                if (!question) return;

                this.userInput.value = '';
                this.userInput.value = question;
                this.userInput.dispatchEvent(new Event('input', { bubbles: true }));
                this.sendButton.click();
            });

            this.choiceButtons.push(button);
            
        });

        this.userInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.sendButton.click();
            }
        });

        this.userInput.addEventListener('input', () => {
            this.userInput.style.height = 'auto';
            this.userInput.style.height = Math.min(this.userInput.scrollHeight, 150) + 'px';
            this.sendButton.disabled = !this.userInput.value.trim();
        });
    }

    async sendMessage() {

        const message = this.userInput.value.trim();
        if (!message) return;

        this.addUserMessage(message);
        this.startLoading();

        this.pushToDataLayer({
            event: 'ask_confused_question_submitted',
            ...(this.conversationId !== null && { askConfusedConversationId: this.conversationId }),
            isFirstMessage: this.conversationId === null,
        });

        try {
            const res = await fetch(this.apiUrl, {
                method: 'POST',
                // Lets the digital-analytics session cookies (HttpOnly, scoped to
                // .confused.com) ride along to api.confused.com so the gateway can
                // stamp them onto the conversation trace (ASK-296/ASK-297).
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Ocp-Apim-Subscription-Key': this.apiSubKey,
                    // Only signed-in journeys carry a token; logged-out sends none and is
                    // treated as unauthenticated by the gateway.
                    ...(this.userJwt && { 'Authorization': `Bearer ${this.userJwt}` }),
                },
                body: JSON.stringify(
                    {
                        message,
                        authenticated: this.getAuthenticatedUser(),
                        ...(this.conversationId !== null && { conversation_id: this.conversationId }),
                        location: {
                            platform: 'web'
                        }
                    }),
            });
            
            this.hideTypingIndicator();

            if (res.status === 404) {
                const data = await res.json();
                this.applyFeedbackGate(data);
                if (data.conversation_id) {
                    const isNew = this.conversationId === null;
                    this.setConversationId(data.conversation_id);
                    this.pushConversationResponseToDataLayer(data.conversation_id, isNew);
                }
                // Rateable even though the text is our own fallback copy rather than the
                // agent's: the customer still asked something and still got this back, and
                // "not helpful" on a no-answer is a signal worth having. Passed defensively
                // because this branch reads a 404 body, which does not always carry an id.
                this.addAiMessage(this.copy.noAnswer, [], [], data.response_id || null);
                return;
            }

            if (!res.ok) {
                const err = new Error(`HTTP ${res.status} ${res.statusText}`);
                err.status = res.status;
                throw err;
            }

            const data = await res.json();
            this.applyFeedbackGate(data);

            const { message: responseMessage, conversation_id, response_id, links, follow_up_questions } = data;

            if (conversation_id) {
                const isNew = this.conversationId === null;
                this.setConversationId(conversation_id);
                this.pushConversationResponseToDataLayer(conversation_id, isNew);
            }

            this.addAiMessage(responseMessage, links, follow_up_questions, response_id || null);

        } catch (err) {
            this.hideTypingIndicator();
            if (err.status === 504) {
                this.addAiMessage(this.copy.timeout);
            } else {
                this.addAiMessage(this.copy.error);
            }
        } finally {
            this.setInputDisabled(false);
            // Skip refocus on touch devices — re-focusing the textarea forces the soft
            // keyboard open, which resizes the visual viewport mid-scroll and causes the
            // AI reply to jump out of view. Desktop users still get the convenience focus.
            if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
                this.userInput.focus();
            }
        }
    }

    // Authenticated means "we hold a signed token for this user", not "a cookie is present".
    // The previous cookie check only asserted that AuthenticatedUser= existed, which anyone
    // could set from devtools. A JWT is only minted server-side for a real signed-in identity
    // (see AskConfusedController), so token presence is the honest signal.
    getAuthenticatedUser() {
        return Boolean(this.userJwt);
    }

    setConversationId(id) {
        this.conversationId = id;
        sessionStorage.setItem('askConfusedConversationId', id);
    }

    // ASK-714: the feedback control's gate. The orchestrator sends
    // `chat_feedback_enabled: true` on every response when the feature is
    // switched on, and OMITS the field entirely when it is off — off is the key
    // being absent, never present-and-false, so an older service and a
    // deliberate "off" look identical from here. Absence therefore means "do
    // not show the control".
    //
    // Strict `=== true` for that reason: absent, false, null, 0 and the string
    // "true" must all read as off. Anything other than a real boolean true is
    // either a service that does not have the feature or a payload we do not
    // understand, and neither is a reason to start showing something.
    //
    // This is the ONLY input to the gate. The control is shown to every
    // customer or to none, regardless of their cookie choices, which is the
    // point: helpfulness is then measured across the whole customer base
    // rather than only the subset who accepted non-essential cookies, and no
    // visitor data reaches a third party to decide it. Nothing here reads the
    // consent cookie, and nothing here talks to Optimizely.
    //
    // No optional chaining deliberately: the gulp pipeline for this module is
    // concat + terser with no Babel step, so whatever is written here ships to
    // the browser as-is, and nothing else in this file relies on that syntax.
    applyFeedbackGate(data) {
        this.feedbackControlEnabled = !!data && data.chat_feedback_enabled === true;
    }

    // The gate's public answer, for the renderer that ASK-554 adds. Always a
    // boolean and never throws, so a caller has no reason to guard it.
    //
    // Note this is only refreshed when a response body was actually parsed —
    // successful responses and the special 404 path (see sendMessage). Every
    // other 4xx/5xx throws before the body is read, so the last known answer
    // stands rather than flapping off on a transient failure. A conversation whose FIRST turn fails that way
    // therefore keeps the `false` it started with, which is the safe direction:
    // the control stays hidden until the server has actually said otherwise.
    isFeedbackControlEnabled() {
        return this.feedbackControlEnabled;
    }

    // Single exit point for analytics. Reads the queue by name at push time rather than
    // caching it, because a GTM container may create the array after we have mounted.
    //
    // EVERY line in here touches host-controlled state, which is why all of it sits
    // inside the try rather than just the push. On a page we do not own:
    //
    //   - window[dataLayerName] may be a getter that throws
    //   - the queue may be a Proxy whose get trap throws
    //   - push may be a throwing getter, so `typeof queue.push` throws while checking
    //   - a plain frozen array throws on push, since modules are strict mode
    //
    // A type check cannot guard the first three, because reading the property in order
    // to check it is itself the risk.
    //
    // This method is called from sendMessage BEFORE its try block, so any escaping throw
    // abandons the send outright: the request is never made, the typing indicator spins
    // forever, and the customer sees nothing at all. Losing an analytics event is always
    // preferable to that.
    pushToDataLayer(payload) {
        try {
            const queue = window[this.dataLayerName];
            if (queue && typeof queue.push === 'function') {
                queue.push(payload);
            }
        } catch {
            // Swallowed on purpose. Nothing about analytics may break the chat.
        }
    }

    pushConversationResponseToDataLayer(id, isNewConversation) {
        this.pushToDataLayer({
            event: isNewConversation ? 'ask_confused_conversation_started' : 'ask_confused_conversation_continued',
            askConfusedConversationId: id,
        });
    }

    addUserMessage(message) {
        const messageDiv = document.createElement('div');
        messageDiv.className = 'message user-message';
        messageDiv.innerHTML = `
            <div class="message-content">
                ${this.escapeHtml(message)}
            </div>
        `;
        this.chatMessages.appendChild(messageDiv);
        // No scroll here — showTypingIndicator() fires immediately after and scrolls
        // once to the bottom, covering both the user message and the typing dots.
        // Two back-to-back smooth scrolls fight each other (and the keyboard) on mobile.
    }

    addAiMessage(message, links = [], choices = [], responseId = null) {
        const messageDiv = document.createElement('div');
        messageDiv.className = 'message ai-message';
        const sanitizedLinks = links
            .map(link => ({ ...link, safeUrl: this.sanitizeUrl(link.url) }))
            .filter(link => link.safeUrl);

        messageDiv.innerHTML = `
            <div class="message-content">
                ${this.formatMessage(message)}
            </div>
        `;

        if (sanitizedLinks.length > 0) {
            const linksWrap = document.createElement('div');
            linksWrap.className = 'message-links';
            const ul = document.createElement('ul');
            sanitizedLinks.forEach(link => {
                const li = document.createElement('li');
                const a = document.createElement('a');
                a.href = link.safeUrl;
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                a.textContent = this.decodeHtmlEntities(link.title);
                li.appendChild(a);
                ul.appendChild(li);
            });
            linksWrap.appendChild(ul);
            messageDiv.querySelector('.message-content').appendChild(linksWrap);
        }

        // ASK-554: appended to messageDiv, NOT into .message-content, deliberately.
        // webex-automation-tests' askConfused.page.ts resolves lastAiReplyContent as
        // `.message-content` inside the last AI reply and lastAiReplyLinks as
        // `.message-links a`; putting the control inside either would change what that
        // Playwright suite reads. `choice-buttons` already sits here for the same reason.
        //
        // The class name also stays clear of `.first-message`, `.initial-questions` and
        // `.typing-indicator`, which that suite's aiReplies() locator excludes on - a
        // clash would quietly stop genuine replies being counted as replies.
        //
        // Inserted before .choice-buttons, not after: the rating belongs with the
        // answer it rates, and any follow-up choices stay last because they are what
        // the customer is meant to act on next.
        //
        // Needs BOTH the server's gate and a response id. No id means there is nothing to
        // attach a rating to: that is the case on the error and timeout paths below, and
        // on any gateway-level failure, which returns ErrorResponse and carries none.
        if (this.isFeedbackControlEnabled() && responseId) {
            messageDiv.appendChild(this.buildFeedbackControl(responseId));
        }

        if (choices && choices.length > 0) {
            const choicesDiv = document.createElement('div');
            choicesDiv.className = 'choice-buttons';
            const choicesHeading = document.createElement('p');
            choicesHeading.className = 'choices-heading';
            choicesHeading.textContent = this.copy.choicesHeading;
            choicesDiv.appendChild(choicesHeading);
            choices.forEach(choice => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'choice-btn btn btn--inverse';
                // create a span here for the text to go in to
                const span = document.createElement('span');
                span.className = 'btn-text';
                span.textContent = this.decodeHtmlEntities(choice.text);
                btn.appendChild(span);
                btn.addEventListener('click', () => this.selectChoice(choice));
                choicesDiv.appendChild(btn);
                this.choiceButtons.push(btn);
            });
            messageDiv.appendChild(choicesDiv);
        }
        this.chatMessages.appendChild(messageDiv);
        this.scrollToMessageTop(messageDiv);
    }

    // ASK-554: the per-response rating control. Built in JS rather than an innerHTML
    // template so the labels cannot be mistaken for markup, matching how choice buttons
    // and links are already built here.
    buildFeedbackControl(responseId) {
        // The gate as it stood when THIS response arrived. The gate is turned off by a
        // later response that omits the flag, but this control stays on screen and
        // clickable, so reading the live gate at click time would report `false` for a
        // control that was only rendered because its own response had it on.
        const gateAtRender = this.isFeedbackControlEnabled();
        const wrap = document.createElement('div');
        wrap.className = 'message-feedback';
        // A group with its own name, so a screen reader reaching the two buttons is told
        // what they are for before it reads either label.
        wrap.setAttribute('role', 'group');
        wrap.setAttribute('aria-label', this.copy.feedbackGroupLabel);

        const labels = { up: this.copy.feedbackUpLabel, down: this.copy.feedbackDownLabel };

        ['up', 'down'].forEach(rating => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'message-feedback__btn';
            // The rating is carried on the element, not in a closure variable read later,
            // so setFeedbackSelection can find the right button without re-deriving order.
            btn.dataset.rating = rating;
            // Starts explicitly 'false' rather than absent: a toggle with no aria-pressed
            // is announced as a plain button, so the unrated state would be invisible to a
            // screen reader rather than merely unset.
            btn.setAttribute('aria-pressed', 'false');
            btn.setAttribute('aria-label', labels[rating]);

            // Empty on purpose. This span exists only as the surface the stylesheet
            // paints the brand thumb onto as a background-image (ASK-789). It carried a
            // thumbs emoji until review on #168: once the icon shipped, a character the
            // CSS then had to hide was a second source of truth for the same mark.
            //
            // The consequence is that the control is INVISIBLE without its stylesheet.
            // That is fine on confused.com, where the icon rules ship with the site, but
            // the standalone widget's own sheet carries no .message-feedback rules yet -
            // ASK-878. CHAT_FEEDBACK_ENABLE_CONTROL must not be turned on until it does.
            const glyph = document.createElement('span');
            glyph.className = 'message-feedback__glyph';
            // Belt and braces rather than load-bearing now: an empty span contributes no
            // accessible name anyway. It keeps that true if anything is ever put back in,
            // which is how the name became "This answer was helpful thumbs up sign".
            glyph.setAttribute('aria-hidden', 'true');
            btn.appendChild(glyph);

            btn.addEventListener('click', () => this.submitFeedback(responseId, rating, wrap, gateAtRender));
            wrap.appendChild(btn);
        });

        return wrap;
    }

    // Applies the selected state. Deliberately not colour: the mark is a background-image,
    // which CSS `color` cannot reach, so a hue change would have nothing to act on. The
    // class drives a fill, border and scale change instead, and aria-pressed carries the
    // same state non-visually (WCAG 1.4.1).
    //
    // Setting both buttons on every call is what makes re-rating work: the previously
    // selected one is cleared in the same pass, so a response can never show two ratings.
    setFeedbackSelection(wrap, rating) {
        wrap.querySelectorAll('.message-feedback__btn').forEach(btn => {
            const selected = btn.dataset.rating === rating;
            btn.classList.toggle('is-selected', selected);
            btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
    }

    submitFeedback(responseId, rating, wrap, gateAtRender) {
        // Optimistic, and that is the point. The highlight is the customer's only
        // confirmation their click registered, so it is applied before the network rather
        // than after it. A rating is telemetry: if the POST fails there is nothing useful
        // to tell them and nothing they could do, while pulling the highlight back would
        // read as the click having been ignored.
        this.setFeedbackSelection(wrap, rating);

        this.pushToDataLayer({
            event: 'ask_confused_response_rated',
            askConfusedRating: rating,
            ...(this.conversationId !== null && { askConfusedConversationId: this.conversationId }),
        });

        // No address or no id means nothing can be posted. The control is still interactive
        // and still shows the selection, because the alternative is a button that silently
        // does nothing, and the dataLayer event above has already fired.
        if (!this.feedbackUrl || !responseId || this.conversationId === null) {
            return;
        }

        // Downstream resolves a re-rating as "last server timestamp wins", so two requests
        // for one response must reach the server in click order. Fired concurrently they
        // can be processed out of order and leave the first choice as the final rating
        // while the UI shows the second. The first rating goes straight out; a later one
        // waits for the previous POST to settle (success or failure) before it is sent.
        // The conversation id is read now, not when the send runs, so a queued rating
        // still belongs to the conversation it was clicked in.
        const conversationId = this.conversationId;
        const previous = this.feedbackInFlight.get(responseId);
        const send = previous
            ? previous.then(() => this.postFeedback(conversationId, responseId, rating, gateAtRender))
            : this.postFeedback(conversationId, responseId, rating, gateAtRender);
        const tracked = send.then(() => {}, () => {});
        this.feedbackInFlight.set(responseId, tracked);
        tracked.then(() => {
            // Only the newest link clears the entry; an older one settling must not
            // drop the tail that a later click is still waiting on.
            if (this.feedbackInFlight.get(responseId) === tracked) {
                this.feedbackInFlight.delete(responseId);
            }
        });
    }

    // Same headers and credentials as sendMessage: the subscription key is what API
    // Management authenticates, the bearer token rides along only when signed in, and
    // credentials: 'include' lets the digital-analytics cookies reach the gateway so a
    // rating can be joined to the session (ASK-296/ASK-297).
    //
    // That flag is all-or-nothing: every cookie scoped to .confused.com accompanies the
    // request, the HttpOnly ccid (X-UniqueCustomerCookieID) among them, exactly as it
    // already does for the chat POST above. A browser cannot send one cookie and withhold
    // another, and omitting credentials would also drop the two digital-analytics cookies
    // the gateway's /feedback handler reads. So "no ccid" is a rule about what this widget
    // attaches - nothing in the body, nothing in the dataLayer event - not a claim about
    // the cookie jar. The handler reads only MiSession-SessionId and
    // X-UniqueCustomerSessionID, and logs no identity at all.
    //
    // Never rejects, per the optimistic contract in submitFeedback.
    postFeedback(conversationId, responseId, rating, gateAtRender) {
        return fetch(this.feedbackUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': this.apiSubKey,
                ...(this.userJwt && { 'Authorization': `Bearer ${this.userJwt}` }),
            },
            body: JSON.stringify({
                conversation_id: conversationId,
                response_id: responseId,
                // The semantic value, never the mark shown for it. A rating keyed on
                // whatever the button displays would split the dataset in two the day
                // design replaces the icon.
                rating,
                platform: 'web',
                chat_feedback_enabled: gateAtRender,
            }),
        }).catch(() => {
            // Swallowed on purpose, per the optimistic contract above. An unhandled
            // rejection here would surface in the console on every flaky network and tell
            // nobody anything actionable.
        });
    }

    selectChoice(choice) {
        this.choiceButtons.forEach(btn => btn.disabled = true);
        this.userInput.value = this.decodeHtmlEntities(choice.text);
        this.sendMessage();
    }

    startLoading() {
        this.userInput.value = '';
        this.userInput.style.height = 'auto';
        this.setInputDisabled(true);
        this.showTypingIndicator();
    }

    showTypingIndicator() {
        this.sendButton.classList.add('loading');

        const typingDiv = document.createElement('div');
        typingDiv.className = 'message ai-message';
        typingDiv.innerHTML = `
            <div class="message-content typing-indicator" aria-hidden="true">
                <div class="typing-circle"></div>
                <div class="typing-circle"></div>
                <div class="typing-circle"></div>
            </div>
        `;
        this._typingIndicator = typingDiv;
        this.chatMessages.appendChild(typingDiv);
        this.scrollToBottom();
    }

    hideTypingIndicator() {
        if (this._typingIndicator) {
            this._typingIndicator.remove();
            this._typingIndicator = null;
        }
        this.sendButton.classList.remove('loading');
    }

    setInputDisabled(disabled) {
        this.choiceButtons.forEach(btn => btn.disabled = disabled);
        this.userInput.disabled = disabled;
        this.sendButton.disabled = disabled || !this.userInput.value.trim();
    }

    scrollToBottom() {
        if (!this.scrollContainer) return;
        this.scrollContainer.scrollTo({ top: this.scrollContainer.scrollHeight, behavior: 'smooth' });
    }

    scrollToMessageTop(el) {
        if (!this.scrollContainer) return;
        // scrollIntoView scrolls EVERY scrollable ancestor including the window, so it drags
        // the whole page instead of just the chat container. Scroll the container directly,
        // using a getBoundingClientRect delta so it works regardless of offsetParent, minus a 70px margin.
        const prefersInstant = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        // Defer one frame so the DOM insertion and any keyboard-driven viewport resize have
        // settled before we read the bounding rects.
        requestAnimationFrame(() => {
            // rect delta works no matter where el sits in the offsetParent chain.
            const delta = el.getBoundingClientRect().top - this.scrollContainer.getBoundingClientRect().top;
            this.scrollContainer.scrollTo({
                top: this.scrollContainer.scrollTop + delta - 70,
                behavior: prefersInstant ? 'auto' : 'smooth',
            });
        });
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    decodeHtmlEntities(text) {
        if (text == null) return '';
        const ta = document.createElement('textarea');
        ta.innerHTML = String(text);
        return ta.value;
    }

    sanitizeUrl(url) {
        try {
            const parsed = new URL(url, window.location.origin);
            if (!['http:', 'https:'].includes(parsed.protocol)) return '';
            return parsed.href;
        } catch {
            return '';
        }
    }

    formatMessage(text) {
        const codeBlocks = [];
        let processed = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
            const idx = codeBlocks.length;
            codeBlocks.push(`<pre><code>${this.escapeHtml(code.trim())}</code></pre>`);
            return `__CB_${idx}__`;
        });

        const inlineCodes = [];
        processed = processed.replace(/`([^`]+)`/g, (_, code) => {
            const idx = inlineCodes.length;
            inlineCodes.push(`<code>${this.escapeHtml(code)}</code>`);
            return `__IC_${idx}__`;
        });

        const markdownLinks = [];
        processed = processed.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, text, url) => {
            const idx = markdownLinks.length;
            const safeUrl = this.sanitizeUrl(url);
            markdownLinks.push(safeUrl
                ? `<a href="${this.escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(text)}</a>`
                : this.escapeHtml(text));
            return `__ML_${idx}__`;
        });

        processed = this.escapeHtml(processed);

        processed = processed.replace(/^#{1,3} (.+)$/gm, '<h4>$1</h4>');

        processed = processed.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
        processed = processed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        processed = processed.replace(/(^|[^*])\*(?!\*)(.+?)\*(?!\*)/g, '$1<em>$2</em>');

        processed = processed.replace(/(^|\n)((?:[-*] .+(?:\n|$))+)/g, (_, pre, block) => {
            const items = block.trim().split('\n')
                .map(item => `<li>${item.replace(/^[-*] /, '')}</li>`).join('');
            return `${pre}<ul>${items}</ul>`;
        });

        processed = processed.replace(/(^|\n)((?:\d+\. .+(?:\n|$))+)/g, (_, pre, block) => {
            const items = block.trim().split('\n')
                .map(item => `<li>${item.replace(/^\d+\. /, '')}</li>`).join('');
            return `${pre}<ol>${items}</ol>`;
        });

        processed = processed
            .split(/\n\n+/)
            .map(block => {
                block = block.trim();
                if (!block) return '';
                if (/^<(h[1-6]|ul|ol|pre|blockquote)/.test(block) || /^__(CB|ML)_/.test(block)) return block;
                return `<p>${block.replace(/\n/g, '<br>')}</p>`;
            })
            .join('');

        codeBlocks.forEach((html, i) => {
            processed = processed.replace(`__CB_${i}__`, html);
        });
        inlineCodes.forEach((html, i) => {
            processed = processed.replace(`__IC_${i}__`, html);
        });
        markdownLinks.forEach((html, i) => {
            processed = processed.replace(`__ML_${i}__`, html);
        });

        return processed;
    }
}

export { ChatInterface };

// The Sitecore auto-init that used to live here now sits in sitecore-bootstrap.js, which
// gulp concatenates onto the end of this file. It was moved because the standalone build
// imports this module: a top-level side effect here binds a SECOND ChatInterface to the
// same container when DOMContentLoaded fires on a GTM page, using an apiUrl that page never
// sets, which falls back to localhost. Keeping this module free of side effects means the
// standalone bundle cannot inherit that behaviour. See ASK-90.

